import * as Yup from "yup";

import { compareDecimalStrings } from "@/shared/utils/decimal";

import { parseAmountInputDecimal } from "../utils/currencyFormatter";

/**
 * Step 1 form shape (amount & conversion).
 *
 * Keys MUST match `DispersionFormData` exactly. Yup validates by key name, so a stale key here is NOT a type
 * error: it silently reports the field as missing and the Continue button never enables. That is precisely
 * what broke when the origin became a currency (`origin` → `origin_currency`).
 *
 * Amounts stay STRINGS end to end. They used to be transformed into numbers for a `min(0.01)` check, which
 * put a `parseFloat` on the one path that feeds ledger amounts; positivity is now compared on the decimal
 * string itself.
 */
interface Step1FormValues {
  origin_currency: string;
  destination: string;
  amount_origin?: string;
  amount_destination?: string;
}

/**
 * Resolved (already-translated) messages for the step-1 schema. The service is framework-agnostic (no React
 * hooks), so the caller resolves them with `t()` and passes the strings in — same pattern the RHF forms use.
 */
export interface Step1SchemaMessages {
  originRequired: string;
  destinationRequired: string;
  amountRequired: string;
  amountPositive: string;
}

/**
 * True for a parseable amount above zero. Tolerates the es-CO notation the input accepts ("1.500,50") by
 * normalizing first, and never converts to `number`.
 */
function isPositiveAmount(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const decimal = parseAmountInputDecimal(value);
  return decimal !== null && compareDecimalStrings(decimal, "0") > 0;
}

const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === "";

export class ValidationSchemaService {
  /** Step 1 validation (amount & conversion). Messages are injected already translated (see the interface). */
  static getStep1ValidationSchema(messages: Step1SchemaMessages): Yup.ObjectSchema<Step1FormValues> {
    return Yup.object().shape({
      origin_currency: Yup.string().required(messages.originRequired),
      destination: Yup.string().required(messages.destinationRequired),
      // At least ONE amount must be provided (mutual exclusivity handled in component)
      amount_origin: Yup.string().when("amount_destination", {
        is: isEmpty,
        then: (schema) =>
          schema
            .required(messages.amountRequired)
            .test("positive-amount", messages.amountPositive, isPositiveAmount),
        otherwise: (schema) => schema.optional(),
      }),
      amount_destination: Yup.string().optional(),
    });
  }
}
